<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="border-b border-gray-200">
    <ul
      class="flex flex-wrap -mb-px text-sm font-medium text-center"
      role="tablist"
      :aria-label="ariaLabel"
    >
      <li
        v-for="tab in visibleTabs"
        :key="tab.value"
        class="me-0 sm:me-2"
        role="presentation"
      >
        <slot
          :name="`tab-${tab.value}`"
          :tab="tab"
          :is-active="isActive(tab.value)"
          :select="() => selectTab(tab)"
        >
          <button
            :id="tabId(tab.value)"
            :aria-label="labelText(tab.label)"
            :aria-selected="isActive(tab.value)"
            :tabindex="isActive(tab.value) ? 0 : -1"
            :disabled="tab.disabled === true"
            :class="tabClasses(tab)"
            type="button"
            role="tab"
            @click="selectTab(tab)"
            @keydown="onKeydown($event, tab)"
          >
            <span
              v-if="tab.icon"
              class="w-4 h-4 me-2 shrink-0 [&>svg]:w-full [&>svg]:h-full"
              aria-hidden="true"
              v-html="tab.icon"
            />
            <template v-if="Array.isArray(tab.label)">
              <span
                v-for="(line, index) in tab.label"
                :key="index"
              >
                <br
                  v-if="index > 0"
                  class="md:hidden"
                >
                {{ line }}
              </span>
            </template>
            <span v-else>{{ tab.label }}</span>
          </button>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, watch } from 'vue';

const BASE_CLASSES = 'inline-flex items-center justify-center p-4 border-b-2 border-transparent rounded-t-lg transition-colors';
const ACTIVE_CLASSES = 'text-tuin-100 border-tuin-100 hover:text-tuin-100 hover:border-tuin-100';
const INACTIVE_CLASSES = 'text-gray-500 hover:text-gray-600 hover:border-gray-300';
const DISABLED_CLASSES = 'opacity-50 cursor-not-allowed';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  tabs: {
    type: Array,
    default: () => []
  },
  id: {
    type: String,
    default: 'tabs'
  },
  ariaLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const visibleTabs = computed(() =>
  props.tabs.filter((tab) => tab && tab.visible !== false && tab.value !== null && tab.value !== undefined)
);

const enabledTabs = computed(() => visibleTabs.value.filter((tab) => tab.disabled !== true));

function labelText(label) {
  return (Array.isArray(label) ? label.join(' ') : label) || '';
}

function tabId(value) {
  return `${props.id}-tab-${String(value).replace(/[^\w-]/g, '-')}`;
}

function isActive(value) {
  return props.modelValue === value;
}

function tabClasses(tab) {
  return [
    BASE_CLASSES,
    tab.class,
    isActive(tab.value) ? ACTIVE_CLASSES : INACTIVE_CLASSES,
    tab.disabled === true ? DISABLED_CLASSES : ''
  ].filter(Boolean);
}

function selectTab(tab) {
  if (tab.disabled === true || isActive(tab.value)) {
    return;
  }
  emit('update:modelValue', tab.value);
  emit('change', tab.value, tab);
}

function focusTab(value) {
  nextTick(() => {
    const element = document.getElementById(tabId(value));
    if (element) {
      element.focus();
    }
  });
}

function onKeydown(event, tab) {
  if (enabledTabs.value.length === 0) {
    return;
  }

  const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!keys.includes(event.key)) {
    return;
  }

  const currentIndex = enabledTabs.value.findIndex((item) => item.value === tab.value);
  if (currentIndex === -1) {
    return;
  }

  event.preventDefault();

  const total = enabledTabs.value.length;
  let nextIndex = currentIndex;

  if (event.key === 'ArrowRight') {
    nextIndex = (currentIndex + 1) % total;
  } else if (event.key === 'ArrowLeft') {
    nextIndex = (currentIndex - 1 + total) % total;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = total - 1;
  }

  const next = enabledTabs.value[nextIndex];
  selectTab(next);
  focusTab(next.value);
}

function ensureValidValue() {
  if (enabledTabs.value.some((tab) => tab.value === props.modelValue)) {
    return;
  }
  if (enabledTabs.value.length === 0) {
    return;
  }
  emit('update:modelValue', enabledTabs.value[0].value);
}

onMounted(ensureValidValue);
watch([visibleTabs, () => props.modelValue], ensureValidValue);

defineExpose({ selectTab, focusTab });
</script>